import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { AdvanceGameRequest, AssignRoleRequest, CloseInvitesRequest, StartGameRequest } from "./apiTypes";
import { setToast } from "./uiReducer";
import type { Game, GameRole, Mystery } from "../types/game";

const SESSION_EXPIRED_TOAST = { title: "Session expired", description: "" };

function clearTokens() {
    console.log("clearTokens");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
}

let url = "https://clue-e0enhucjdcduakgy.eastus-01.azurewebsites.net/";
const dev = true;
if (dev && !import.meta.env.PROD) {
    url = "http://127.0.0.1:8000/";
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: url,
    prepareHeaders: (headers) => {
        const token = localStorage.getItem("access_token");
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
    },
});

async function baseQueryWithReauth(
    args: Parameters<typeof rawBaseQuery>[0],
    api: Parameters<typeof rawBaseQuery>[1],
    extraOptions: Parameters<typeof rawBaseQuery>[2]
) {
    let result = await rawBaseQuery(args, api, extraOptions);
    console.log(result)
    if (result.error?.status === 401) {
        const requestUrl = typeof args === "string" ? args : (args as { url?: string } | null)?.url ?? "";
        const isRefreshRequest = String(requestUrl).includes("token/refresh");

        if (isRefreshRequest) {
            clearTokens();
            api.dispatch(setToast(SESSION_EXPIRED_TOAST));
            window.location.href = "/login";
            return result;
        }

        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
            clearTokens();
            api.dispatch(setToast(SESSION_EXPIRED_TOAST));
            window.location.href = "/login";
            return result;
        }

        const refreshResult = await rawBaseQuery(
            {
                url: "/accounts/token/refresh",
                method: "POST",
                body: { refresh: refreshToken },
            },
            api,
            extraOptions
        );

        if (refreshResult.error) {
            clearTokens();
            api.dispatch(setToast(SESSION_EXPIRED_TOAST));
            window.location.href = "/login";
            return result;
        }

        const access = (refreshResult.data as { access?: string } | undefined)?.access;
        if (access) localStorage.setItem("access_token", access);

        result = await rawBaseQuery(args, api, extraOptions);
    }

    return result;
}

export const apiSlice = createApi({
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getGames: builder.query({
            query: () => "/games/all-games" 

        }),
        getGame: builder.query<Game, string>({
            query: (id: string) => `/games/games?gameId=${id}`
        }),
        getMysteries: builder.query<Mystery[], void>({
            query: () => "/games/mysteries"
        }),
        getMyGames: builder.query<Game[], void>({
            query: () => "/games/my-games"
        }),
        createGame: builder.mutation({
            query: (data: { name: string; mystery: string; host_is_playing?: boolean }) => ({
                url: "/games/games",
                method: "POST",
                body: data
            })
        }),
        getUser: builder.query({
            query: () => "/accounts/user"
        }),
        updateUser: builder.mutation({
            query: (data: any) => ({
                url: "/accounts/user",
                method: "PATCH",
                body: data
            })
        }),
        startLogin: builder.mutation({
            query: (phone: string) => ({
                url: "/accounts/login",
                method: "PATCH",
                body: {
                    phone: phone
                }
            })
        }),
        verifyLogin: builder.mutation({
            query: (data: any) => ({
                url: "/accounts/login",
                method: "POST",
                body: {
                    phone: data.phone,
                    verification_code: data.verification_code
                }
            })
        }),
        startEmailLogin: builder.mutation({
            query: (data: { phone: string; email: string }) => ({
                url: "/accounts/login/email",
                method: "PATCH",
                body: data
            })
        }),
        verifyEmailLogin: builder.mutation({
            query: (data: { phone: string; verification_code: string }) => ({
                url: "/accounts/login/email",
                method: "POST",
                body: data
            })
        }),
        register: builder.mutation({
            query: (data: any) => ({
                url: "/games/register",
                method: "POST",
                body: data
            })
        }),
        startGame: builder.mutation({
            query: (data: StartGameRequest) => ({
                url: "/games/start",
                method: "POST",
                body: data
            })
        }),
        advance: builder.mutation({
            query: (data: AdvanceGameRequest) => ({
                url: "/games/advance",
                method: "POST",
                body: data
            })
        }),
        assignRole: builder.mutation({
            query: (data: AssignRoleRequest) => ({
                url: "/games/assign-role",
                method: "POST",
                body: data
            })
        }),
        closeInvites: builder.mutation({
            query: (data: CloseInvitesRequest) => ({
                url: "/games/close-invites",
                method: "POST",
                body: data
            })
        }),
        getRoles: builder.query<GameRole[], string>({
            query: (gameId: string) => `/games/roles?game_id=${gameId}`
        }),
        getPlayer: builder.query({
            query: (access_code: string) => `/games/player?access_code=${access_code}`
        }),
        updatePlayer: builder.mutation({
            query: (data: any) => ({
                url: `/games/player/${data.objectId}`,
                method: "PUT",
                body: data
            })
        }),
        randomAssign: builder.mutation({
            query: (data: any) => ({
                url: "/games/random-assign",
                method: "POST",
                body: data
            })
        }),
        rsvp: builder.mutation({
            query: (data: { game: string; status: string }) => ({
                url: "/games/rsvp",
                method: "POST",
                body: data
            })
        }),
    }),
});

export const {
    useGetUserQuery,
    useLazyGetUserQuery,
    useGetGamesQuery,
    useGetGameQuery,
    useGetMysteriesQuery,
    useGetMyGamesQuery,
    useCreateGameMutation,
    useRegisterMutation,
    useUpdateUserMutation,

    useGetPlayerQuery,
    useLazyGetPlayerQuery,
    useAssignRoleMutation,
    useCloseInvitesMutation,
    useGetRolesQuery,
    useStartGameMutation,
    useLazyGetGameQuery,
    useUpdatePlayerMutation,
    useRandomAssignMutation,
    useAdvanceMutation,
    useStartLoginMutation,
    useVerifyLoginMutation,
    useStartEmailLoginMutation,
    useVerifyEmailLoginMutation,
    useRsvpMutation,

} = apiSlice;
export default apiSlice;
